// Brand resolution for the admin console (multi-tenant merge M3d).
//
// The HOSTNAME carries the brand: admin.tippingiq.com is the TippingIQ
// console, admin.platinumiq.ai the PlatinumIQ one. One login per hostname,
// so an admin can have both open in separate tabs and always knows which is
// which from the URL and the header badge. There is no in-app toggle that
// silently re-points the same tab at the other brand's data; "switching" is a
// link to the other brand's hostname.

export interface AccessibleBrand {
  brandKey: string;
  name: string;
  primaryDomain: string;
  primaryColour: string | null;
  secondaryColour: string | null;
  logoUrl: string | null;
}

export interface MeResponse {
  adminUserId: number | null;
  email: string | null;
  role: string;
  actingBrand: string | null;
  brands: AccessibleBrand[];
  /** Every brand's primary domain, including brands this admin is not offered. */
  brandDomains?: string[];
}

/**
 * Hostname is the brand's primary domain or any subdomain of it (case-insensitive).
 * When one brand's domain sits under another's (naki-preview.tippingiq.com under
 * tippingiq.com), the longest matching domain wins.
 */
export function matchBrandByHostname(hostname: string, brands: AccessibleBrand[]): AccessibleBrand | null {
  const host = hostname.toLowerCase();
  let best: AccessibleBrand | null = null;
  for (const b of brands) {
    const domain = b.primaryDomain.toLowerCase();
    if ((host === domain || host.endsWith(`.${domain}`)) && domain.length > (best?.primaryDomain.length ?? -1)) best = b;
  }
  return best;
}

/**
 * Which brand this console is acting as. Hostname wins; when it names none of
 * the admin's accessible brands (localhost, a CloudFront preview host) fall
 * back to the brand the server resolved the request to - which is null when
 * the admin has no access to the backend's default brand, so a PlatinumIQ-only
 * admin landing on the TippingIQ console gets no brand rather than the wrong one.
 */
export function resolveConsoleBrand(hostname: string, me: MeResponse): AccessibleBrand | null {
  // The host belongs to the brand with the longest matching domain among ALL
  // brands, not just the ones offered: admin.naki-preview.tippingiq.com is
  // Naki's even for an admin who only has TippingIQ, and must stop there.
  const host = hostname.toLowerCase();
  const domains = [...(me.brandDomains ?? []), ...me.brands.map((b) => b.primaryDomain)].map((d) => d.toLowerCase());
  const owner = domains.filter((d) => host === d || host.endsWith(`.${d}`)).sort((a, b) => b.length - a.length)[0];
  if (owner) return me.brands.find((b) => b.primaryDomain.toLowerCase() === owner) ?? null;
  return me.brands.find((b) => b.brandKey === me.actingBrand) ?? null;
}

/**
 * URL of the same page on another brand's console: swap the primary-domain
 * suffix of the current host, keeping the subdomain prefix, port and path.
 * Returns null when the current host isn't on `from`'s domain (localhost), as
 * there is then no hostname to derive the other console from.
 */
export function switchBrandUrl(location: Location, from: AccessibleBrand, to: AccessibleBrand): string | null {
  const host = location.hostname.toLowerCase();
  const fromDomain = from.primaryDomain.toLowerCase();
  if (host !== fromDomain && !host.endsWith(`.${fromDomain}`)) return null;
  const newHost = host.slice(0, host.length - fromDomain.length) + to.primaryDomain.toLowerCase();
  const port = location.port ? `:${location.port}` : '';
  return `${location.protocol}//${newHost}${port}${location.pathname}${location.search}`;
}
